import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CaixaDoBancoDados, ConexaoDoBanco, ContaDoBanco, MovimentoDoBanco,
  getCaixaDoBanco, buscarConexoesDoBanco, sincronizarBanco, ligarContaDoBanco, definirModoDoBanco,
  aceitarMovimentoDoBanco, confirmarMesmoDoBanco, ignorarMovimentoDoBanco, separarMovimentoDoBanco,
  corrigirMovimentoDoBanco, desfazerMovimentoDoBanco, desconectarBanco, createCategory,
  aceitarMovimentosDoBancoEmLote, getLinkParaConectarBanco, getRetratoDoCartao,
  dispensarEstornoDoBanco, EstornadoDoBanco, getRaioXDoBanco, acertarParceladasDoBanco,
  AcessoAoBanco, getAcessoAoBanco, getFormatoDasConexoes,
  iniciarConexaoDoBanco, confirmarConexaoDoBanco,
  getSuporteDoBanco, enviarPedidoDeSuporte, MeuPedidoDeSuporte, TipoDePedidoDeSuporte,
  vincularFaturaDoBanco, desvincularFaturaDoBanco, VinculoDeFatura,
} from '../services/api';
import { consequenciaDe, mesesParaEscolher, mesPorExtenso, opcaoSugerida, seguraOLancamento } from '../utils/faturaDoBanco';
import { Category } from '../types/Category';
import { PaymentMethod } from '../types/PaymentMethod';
import { Transaction } from '../types/Transaction';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useBotaoOcupado } from '../utils/botaoOcupado';
import { useConfirmar } from '../contexts/Confirmacao';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { brlOrMasked } from '../utils/format';
import { LIMITES } from '../utils/limites';
import { Info } from 'lucide-react';
import ComoFuncionaOpenFinance from './ComoFuncionaOpenFinance';
import OfertaDoOpenFinance from './OfertaDoOpenFinance';
import './CaixaDoBanco.css';

/**
 * O que veio do banco.
 *
 * ── O desenho, depois de ela ver a primeira versão (06/10) ───────────────
 *
 * A primeira tela pedia categoria e método em cada linha. Ela respondeu:
 *
 *   - o MÉTODO o banco já sabe. A tela pergunta uma vez por CONTA do banco
 *     ("que método é este?"), e nunca mais;
 *   - a CATEGORIA o app descobre. Só aparece seletor quando ele não sabe, e a
 *     resposta dela vira regra para aquele lugar;
 *   - cada pessoa escolhe entre LANÇAR SOZINHO e SER AVISADA.
 *
 * ── A ordem da tela é a ordem do que pede ela ────────────────────────────
 *
 *   1. Conta sem método      sem isto nada pode ser lançado.
 *   2. Precisa de você       o provável ("é o seu aluguel?") e o que o app
 *                            não soube categorizar.
 *   3. Novos                 o app sabe tudo; falta só o toque dela. No modo
 *                            automático, isto fica vazio daqui pra frente.
 *   4. Lançados sozinhos     para conferir, trocar a categoria ou desfazer.
 *   5. Já lançados por você  recolhidos.
 *
 * ── O nome ────────────────────────────────────────────────────────────────
 *
 * A primeira versão se chamava "Do banco". Ela trocou para "Open Finance" em
 * 06/10: é o nome que os bancos usam na tela de autorização e na propaganda,
 * então é por ele que a pessoa reconhece o que é e confia. O nome técnico
 * fica no título e no menu; as frases de dentro continuam em língua de gente
 * ("o que veio do seu banco").
 */

interface Props {
  onClose: () => void;
  categories: Category[];
  paymentMethods: PaymentMethod[];
  /** Os lançamentos dela — para mostrar com QUAL um movimento parece casar. */
  transactions: Transaction[];
  /** Algo virou (ou deixou de ser) lançamento: o app recarrega lista, saldo e projeção. */
  onLancou: () => void;
  /** Um método foi cadastrado daqui: a lista de métodos do app precisa saber. */
  onMetodosMudaram: () => void;
  /** Uma categoria foi criada daqui: a lista de categorias do app precisa saber. */
  onCategoriasMudaram: () => void;
  /** Quem cuida do app. Só ela vê a ferramenta de suporte (o retrato do cartão). */
  admin?: boolean;
  /** A caixa que o app buscou de antemão. Com ela a janela abre sem esperar. */
  dadosIniciais?: CaixaDoBancoDados | null;
  /** O saldo que o app mostra hoje. `null` = ainda não chegou. */
  saldoDoApp: number | null;
  /** Declara "meu saldo agora é este" — o mesmo caminho do Ajustar saldo. */
  onUsarComoSaldo: (valor: number) => Promise<void>;
  hideValues?: boolean;
  /** Abre "Meu plano": com a cobrança ligada, o Open Finance se pede trocando para o Completo. */
  onVerPlano?: () => void;
}

/** O que o provedor diz da conexão, em português, e se pede ação dela. */
const ESTADO_DA_CONEXAO: Record<string, { texto: string; problema: boolean }> = {
  UPDATED: { texto: 'em dia', problema: false },
  UPDATING: { texto: 'atualizando…', problema: false },
  LOGIN_ERROR: { texto: 'o banco pediu novo login', problema: true },
  OUTDATED: { texto: 'a autorização venceu', problema: true },
  WAITING_USER_INPUT: { texto: 'o banco pede uma confirmação sua', problema: true },
};

/** O valor do seletor que quer dizer "cadastre um método novo com o nome da conta". */
const CADASTRAR = '__cadastrar__';

/**
 * "Não perguntar de novo" ao ignorar. Preferência do aparelho, não da conta:
 * é conveniência, e se o navegador não guardar, o pior que acontece é a
 * pergunta voltar.
 */
const CHAVE_IGNORAR = 'fintracking:banco:ignorar-sem-perguntar';
const ignoraSemPerguntar = (): boolean => {
  try { return localStorage.getItem(CHAVE_IGNORAR) === '1'; } catch { return false; }
};
const lembrarDeNaoPerguntar = (): void => {
  try { localStorage.setItem(CHAVE_IGNORAR, '1'); } catch { /* sem armazenamento: pergunta de novo */ }
};

/** 'AAAA-MM' → "outubro". Ao meio-dia, para o fuso não puxar o mês para trás. */
const nomeDoMes = (mes: string): string =>
  new Date(`${mes}-15T12:00:00`).toLocaleDateString('pt-BR', { month: 'long' });

/** "A, B e C", para frase corrida. */
const emLista = (itens: string[]): string =>
  (itens.length < 2 ? itens.join('') : `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`);

/** Enquanto o servidor ainda lê um banco: de quanto em quanto a tela olha, e quantas vezes. Um minuto no total. */
const PAUSA_DA_ESPERA_MS = 5000;
const VEZES_DE_ESPERA = 12;

/** Quantos movimentos vão em cada chamada do lote, e quantas chamadas no máximo. */
const POR_REMESSA = 25;
const MAX_DE_REMESSAS = 8;

/** Quanto tempo o check de "lançado" fica na tela, em ms. */
const TEMPO_DO_FEITO = 1200;

/** O valor do seletor de categoria que quer dizer "crie uma agora". */
const NOVA_CATEGORIA = '__nova__';

/** A cor da categoria criada daqui: a próxima da paleta, para não repetir a vizinha. */
const CORES_DE_CATEGORIA = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#6366f1', '#a855f7', '#ec4899', '#64748b',
];

const dataCurta = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/** "em 06/10 às 14:32", no fuso do Brasil. */
const quandoFoi = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const fuso = { timeZone: 'America/Sao_Paulo' } as const;
  return `em ${d.toLocaleDateString('pt-BR', { ...fuso, day: '2-digit', month: '2-digit' })} às ${
    d.toLocaleTimeString('pt-BR', { ...fuso, hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Um "i" que abre a explicação logo abaixo da linha (22/10).
 *
 * Três frases moravam sempre abertas na tela — de quando é o extrato, por que
 * o saldo ainda não é igualado, quando o banco é lido — e a tela virava texto.
 * Agora ficam atrás de um ícone, na linha do que explicam. `linha` recebe o
 * botão para pô-lo onde a linha quiser; o texto vem embaixo dela.
 */
const ComInfo: React.FC<{
  rotulo: string;
  texto: React.ReactNode;
  linha: (botao: React.ReactNode) => React.ReactNode;
}> = ({ rotulo, texto, linha }) => {
  const [aberto, setAberto] = useState(false);
  const botao = (
    <button type="button" className="bco-info" aria-label={rotulo} title={rotulo}
            aria-expanded={aberto} onClick={() => setAberto(v => !v)}>
      <Info size={15} aria-hidden="true" />
    </button>
  );
  return (
    <>
      {linha(botao)}
      {aberto && <p className="bco-info-texto" role="note">{texto}</p>}
    </>
  );
};

const VAZIA: CaixaDoBancoDados = { conexoes: [], contas: [], pendentes: [], casados: [], lancados: [] };

const CaixaDoBanco: React.FC<Props> = ({
  onClose, categories: categoriasDoApp, paymentMethods, transactions, onLancou, onMetodosMudaram,
  onCategoriasMudaram, saldoDoApp, onUsarComoSaldo, hideValues, admin, dadosIniciais = null, onVerPlano,
}) => {
  /*
   * Abre com o que o app já buscou, sem "Carregando…". A leitura de sempre
   * roda logo em seguida e troca pelos dados frescos, sem piscar a tela. Só
   * sem a prévia (a primeira vez, ou se ela falhou) aparece o esqueleto.
   */
  const [dados, setDados] = useState<CaixaDoBancoDados>(dadosIniciais ?? VAZIA);
  const [carregando, setCarregando] = useState(!dadosIniciais);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [verCasados, setVerCasados] = useState(false);
  const [verFaturas, setVerFaturas] = useState(false);
  /**
   * Pagamento de fatura (13/10). `naoEFatura`: ela disse que aquele movimento
   * não é fatura, e ele volta a ser um movimento comum nesta tela.
   * `escolhaDeFatura`: o cartão e o mês que ela está escolhendo à mão.
   */
  const [naoEFatura, setNaoEFatura] = useState<Set<string>>(new Set());
  const [escolhaDeFatura, setEscolhaDeFatura] = useState<Record<string, { metodoId: string; mes: string }>>({});
  /** O servidor respondeu que o Open Finance não está ligado para esta conta. */
  const [desligado, setDesligado] = useState<null | 'desligado' | 'iniciando'>(null);
  /** A página que explica o Open Finance, no lugar da lista. */
  const [ajuda, setAjuda] = useState(false);
  /** O banco da aba aberta. Vazio = o primeiro. Só importa com mais de um banco. */
  const [bancoAtivo, setBancoAtivo] = useState('');
  /** O que ela mudou num movimento antes de lançar. Sem entrada = a sugestão do app. */
  const [edicoes, setEdicoes] = useState<Record<string, { categoryId?: string; nome?: string; aberto?: boolean }>>({});
  /**
   * A categoria sendo criada, e em qual movimento. Uma por vez.
   *
   * Pedido dela: quem está categorizando o extrato e não acha a categoria não
   * devia ter de fechar esta tela, criar em outro lugar e voltar.
   */
  const [nova, setNova] = useState<{ de: string; nome: string; criando?: boolean; erro?: string } | null>(null);
  /**
   * As criadas daqui, até a lista do app recarregar. Sem isto o seletor
   * ficaria um instante apontando para uma categoria que ele ainda não tem.
   */
  const [criadas, setCriadas] = useState<Category[]>([]);
  /**
   * A lista de categorias desta tela: a que veio COM a caixa, mais a do app e
   * as criadas aqui.
   *
   * A do servidor manda. A do app é carregada na abertura junto com as
   * transações, e quem abria o Open Finance antes de ela chegar via todo
   * "Novo" como "Sem categoria" e os seletores vazios — a sugestão estava
   * certa e faltava o nome. A que vem com a caixa é a mesma que o servidor
   * usou para sugerir, então terminou de carregar, tem nome.
   */
  const categories = (() => {
    const todas = new Map<string, { id: string; name: string }>();
    for (const c of [...(dados.categorias ?? []), ...categoriasDoApp, ...criadas]) {
      if (!todas.has(c.id)) todas.set(c.id, { id: c.id, name: c.name });
    }
    return Array.from(todas.values());
  })();
  /**
   * O que ela está digitando em "mudar nome ou categoria", antes de confirmar.
   *
   * Separado de `edicoes` para existir um "cancelar": a mudança só vale depois
   * do Confirmar, e até lá o Lançar não aparece — senão ele lançaria um
   * rascunho que ela ainda não decidiu.
   */
  const [rascunhos, setRascunhos] = useState<Record<string, { nome: string; categoryId: string }>>({});
  /**
   * Os "Novos" marcados para lançar de uma vez.
   *
   * Pedido dela: a primeira leitura traz dezenas de movimentos que o app já
   * sabe categorizar, e tocar "Lançar" em cada um é o trabalho que o Open
   * Finance veio tirar.
   */
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  /**
   * A tela travada enquanto um lançamento entra, e o "feito" no fim.
   *
   * Pedido dela. Sem a trava dava para marcar, editar ou lançar outro no meio
   * do caminho — e o que ela mexesse valeria para uma lista que estava a um
   * instante de ser trocada pela resposta do servidor.
   */
  const [trava, setTrava] = useState<{ fase: 'lancando' | 'feito'; texto: string } | null>(null);
  /** O endereço para conectar mais um banco, depois de pedido. */
  const [linkDeConexao, setLinkDeConexao] = useState<string | null>(null);
  /**
   * O suporte: o formulário que trocou o "gere, copie e mande".
   *
   * `null` = ainda não sei se existe para esta conta. O servidor diz, e sem a
   * tabela de pedidos a linha do suporte simplesmente não aparece.
   */
  const [suporte, setSuporte] = useState<{ email: string | null; pedidos: MeuPedidoDeSuporte[] } | null>(null);
  const [pedido, setPedido] = useState<{
    aberto: boolean; tipo: TipoDePedidoDeSuporte; conexaoId: string; mensagem: string; valor: string; enviado: boolean;
  }>({ aberto: false, tipo: 'faltou', conexaoId: '', mensagem: '', valor: '', enviado: false });
  /** O valor que ela quer achar no extrato do banco ("63,35"). */
  const [valorProcurado, setValorProcurado] = useState('');
  /** O retrato do cartão, em texto, pronto para copiar. */
  const [retrato, setRetrato] = useState<string | null>(null);
  /** A escolha de método para cada conta ainda sem método. */
  const [metodoDaConta, setMetodoDaConta] = useState<Record<string, string>>({});
  const caixa = useRef<HTMLDivElement>(null);
  const confirmar = useConfirmar();
  const fmt = (v: number) => brlOrMasked(v, !!hideValues);

  useTravarScroll();
  useFocoNoModal(caixa);
  // O botão tocado ganha a barrinha enquanto a chamada dele durar.
  useBotaoOcupado(caixa, !!ocupado);
  useFechavelComVoltar(true, onClose);

  /**
   * A situação do acesso (08/10): a dona da chave, liberada (o Completo), ou
   * quem não tem plano com banco. `null` = não sei (o Open Finance fechado, ou a consulta
   * falhou), e aí a tela se comporta como sempre se comportou.
   */
  const [acesso, setAcesso] = useState<AcessoAoBanco | null>(dadosIniciais?.acesso ?? null);
  const carregarAcesso = useCallback(async (): Promise<AcessoAoBanco | null> => {
    try { const a = await getAcessoAoBanco(); setAcesso(a); return a; } catch { setAcesso(null); return null; }
  }, []);

  const carregar = useCallback(async () => {
    try {
      const caixaLida = await getCaixaDoBanco();
      setDados(caixaLida);
      setDesligado(null);
      // A situação do acesso vem junto com a caixa. A chamada à parte fica só
      // para um servidor antigo que ainda não a mande.
      if (caixaLida.acesso) setAcesso(caixaLida.acesso); else void carregarAcesso();
    } catch (e) {
      /*
       * 404 é o servidor dizendo que o Open Finance não existe para esta conta
       * AGORA: saiu da lista de liberados, faltou a chave, ou o servidor subiu
       * sem achar as tabelas. A tela mostrava a apresentação de quem nunca
       * conectou, com "Não disponível" solto embaixo — parecia que os bancos
       * dela tinham sumido. Não sumiram: a leitura é que está desligada.
       */
      const status = (e as { response?: { status?: number } })?.response?.status;
      // 503 = o servidor ainda está subindo (logo depois de um deploy). Não é
      // "desligado": é esperar um instante.
      if (status === 503) setDesligado('iniciando');
      else if (status === 404) {
        // Sem acesso à caixa. Se a porta está aberta, ela vê a OFERTA (pedir,
        // pagar, a fila); só sem isso é que o Open Finance está "desligado".
        const a = await carregarAcesso();
        setDesligado(a && a.estado !== 'dona' ? null : 'desligado');
      }
      else setErro(mensagemDeErro(e, 'Não consegui carregar o que veio do banco.'));
    } finally {
      setCarregando(false);
    }
  }, [carregarAcesso]);

  useEffect(() => { void carregar(); }, [carregar]);

  // À parte da caixa, e falha à parte: o suporte não carregar não pode
  // impedir ninguém de ver os próprios lançamentos.
  const carregarSuporte = useCallback(async () => {
    try {
      const r = await getSuporteDoBanco();
      setSuporte(r.disponivel ? { email: r.email ?? null, pedidos: r.pedidos } : null);
    } catch { setSuporte(null); }
  }, []);
  useEffect(() => { void carregarSuporte(); }, [carregarSuporte]);

  /** Roda uma ação mostrando o que está ocupado e o erro, se houver. */
  const fazer = async (chave: string, acao: () => Promise<void>) => {
    setOcupado(chave); setErro(''); setAviso('');
    try { await acao(); } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui fazer isso agora.'));
      /*
       * O erro é escrito no alto da tela, e quem tocou num botão lá embaixo
       * não o via: a ação falhava e parecia que nada tinha acontecido. Foi
       * assim que um lote recusado pelo servidor passou por "não lançou".
       */
      setTimeout(() => caixa.current?.querySelector('.bco-erro')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
    } finally { setOcupado(null); }
  };

  /**
   * Banco que não foi lido é ERRO na tela, com o motivo. "Nada novo" só pode
   * ser dito de um banco que foi lido de fato.
   */
  const dizerFalhas = (falhas?: { banco: string; motivo: string }[]): boolean => {
    if (!falhas || falhas.length === 0) return false;
    // O motivo técnico vai para o fim, entre parênteses: quem lê primeiro é
    // ela, e o que ela precisa saber é o que fazer.
    setErro(falhas.map(f => (f.motivo === 'ainda lendo'
      // Não é falha: a leitura segue no servidor. Dizer "não consegui" seria falso.
      ? `O ${f.banco} ainda está sendo lido no servidor. Toque em Atualizar daqui a um minuto.`
      : `Não consegui ler o ${f.banco} agora (${motivoCurto(f.motivo)}). Tente de novo em alguns minutos.`)).join(' '));
    setTimeout(() => caixa.current?.querySelector('.bco-erro')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
    return true;
  };

  /** O motivo em poucas palavras. O código do provedor fica só no log do servidor. */
  const motivoCurto = (motivo: string): string =>
    (/provedor respondeu/.test(motivo) ? 'o serviço do banco falhou' : motivo);

  const resumo = (r: { novos: number; lancados?: number; semContagem?: boolean }) => {
    // A leitura terminou no servidor depois da resposta: a contagem não voltou,
    // e "nada novo" poderia ser mentira. O que chegou está na lista.
    if (r.semContagem && !r.novos) return 'Leitura concluída.';
    if (r.lancados) return `${r.lancados} lançado(s) sozinho(s), ${Math.max(r.novos - r.lancados, 0)} esperando você.`;
    return r.novos === 0 ? 'Nada novo desde a última leitura.' : `${r.novos} movimento(s) novo(s).`;
  };

  /**
   * Lê os bancos, UM por chamada, com a tela travada dizendo qual.
   *
   * O servidor dá um tempo limitado a cada requisição, e três bancos numa só
   * passavam dele: nenhum era lido e a tela mostrava um erro genérico. De um
   * em um cabe sempre, ela vê o andamento ("Lendo o Itaú… 2 de 3"), e o banco
   * que falhar não leva os outros junto: ele entra em `falhas`, com o motivo.
   */
  const lerBancos = async (conexoes: { id: string; banco: string }[], releitura = false) => {
    const soma = { novos: 0, lancados: 0, semContagem: false, falhas: [] as { banco: string; motivo: string }[] };
    try {
      for (let i = 0; i < conexoes.length; i++) {
        const c = conexoes[i];
        setTrava({
          fase: 'lancando',
          texto: conexoes.length > 1 ? `Lendo o ${c.banco}… ${i + 1} de ${conexoes.length}` : `Lendo o ${c.banco}…`,
        });
        try {
          const antes = dados.conexoes.find(x => x.id === c.id)?.sincronizadaEm ?? null;
          const r = await sincronizarBanco(c.id, releitura);
          soma.novos += r.novos;
          soma.lancados += r.lancados;
          soma.falhas.push(...(r.falhas ?? []));
          /*
           * O servidor respondeu "ainda lendo": a leitura continua lá. Em vez
           * de chamar isso de erro, a tela espera, olhando de tempos em tempos
           * se a hora da última leitura desse banco mudou.
           */
          if (r.emAndamento) {
            setTrava({ fase: 'lancando', texto: `O ${c.banco} está demorando. Continuo lendo…` });
            let terminou = false;
            for (let vez = 0; vez < VEZES_DE_ESPERA && !terminou; vez++) {
              await new Promise(res => setTimeout(res, PAUSA_DA_ESPERA_MS));
              try {
                const agora = await getCaixaDoBanco();
                terminou = (agora.conexoes.find(x => x.id === c.id)?.sincronizadaEm ?? null) !== antes;
              } catch { /* tenta na próxima volta */ }
            }
            if (terminou) soma.semContagem = true;
            else soma.falhas.push({ banco: c.banco, motivo: 'ainda lendo' });
          }
        } catch (e) {
          // Sem repetir a frase do servidor: ela já diz "tente de novo", e a
          // tela diz também. Aqui vai só o que distingue um caso do outro.
          const status = (e as { response?: { status?: number } })?.response?.status;
          soma.falhas.push({ banco: c.banco, motivo: status === 503 ? 'demorou demais' : 'o servidor não respondeu' });
        }
      }
    } finally { setTrava(null); }
    return soma;
  };

  const buscarBancos = () => fazer('buscar', async () => {
    const antes = dados.conexoes.length;
    const achados = await buscarConexoesDoBanco();
    const conhecidosAntes = new Set(dados.conexoes.map(c => c.id));
    // Só os bancos NOVOS são lidos agora: os outros têm o Atualizar.
    const lido = await lerBancos(achados.conexoesLidas.filter(c => !conhecidosAntes.has(c.id)));
    const r = { ...achados, ...lido };
    await carregar();
    onMetodosMudaram();
    dizerFalhas(lido.falhas);
    if (r.conexoesLidas.length === 0) {
      setAviso('Não achei nenhum banco conectado na sua conta do Banco MCP.');
    } else if (antes > 0 && r.conexoesLidas.length <= antes) {
      // Ela veio buscar OUTRO banco e não há: "nada novo" não diria o que falta.
      setAviso('Não achei banco novo. Conecte-o antes na sua conta do Banco MCP (app.mcp.ai) e busque de novo.');
    } else {
      setAviso(`${antes > 0 ? 'Banco novo conectado. ' : ''}${resumo(r)}`);
      /*
       * Conectou: os três passos de "como conectar" já cumpriram o papel.
       * Ficando abertos, a tela continuava pedindo para ela conectar o banco
       * que acabou de entrar. E a aba vai para o banco novo, que é o que ela
       * veio ver.
       */
      setLinkDeConexao(null);
      const conhecidos = new Set(dados.conexoes.map(c => c.id));
      const novo = r.conexoesLidas.find(c => !conhecidos.has(c.id));
      if (novo) setBancoAtivo(novo.id);
    }
    setTimeout(() => caixa.current?.querySelector('.bco-aviso')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
  });

  /**
   * "Usar como meu saldo": o saldo do banco vira o ponto de partida do app.
   *
   * ── Usa o número que está na tela (13/10) ───────────────────────────────
   *
   * Até aqui o toque relia os bancos antes de perguntar, para o saldo ser o
   * de agora. Pedido dela: a tela já mostra o valor que vai ser usado, e
   * esperar uma releitura para confirmar o mesmo número não faz sentido. Quem
   * quer o saldo mais novo toca em Atualizar antes.
   *
   * Em troca, a confirmação diz QUANDO o saldo foi lido: o número pode ser o
   * das 6h, e ela decide sabendo disso.
   *
   * ── Nunca sozinho ────────────────────────────────────────────────────────
   *
   * O saldo do app é UM, de todas as contas dela. O do banco é o das contas
   * conectadas aqui. Copiar um sobre o outro sem ela pedir daria um número
   * errado, calado, para quem tem dinheiro em outro lugar.
   */
  const usarSaldoDoBanco = () => fazer('saldo', async () => {
    // O botão só aparece com a lista resolvida; a conferência fica aqui para
    // o caso de a tela ter mudado entre o desenho e o toque.
    if (dados.pendentes.length > 0) {
      setAviso('Há movimento do banco esperando. Resolva-o primeiro, e depois acerte o saldo.');
      return;
    }
    const fresco = dados;
    const contas = fresco.contas.filter(c => c.tipo === 'BANK' && typeof c.saldo === 'number');
    if (contas.length === 0) throw new Error('sem saldo');
    const soma = Math.round(contas.reduce((s, c) => s + (c.saldo ?? 0), 0) * 100) / 100;

    // "Itaú (R$ 15.871,96), Mercado Pago (R$ 8,44) e Santander (R$ 81,98)".
    const deOnde = emLista(contas.map(c =>
      `${fresco.conexoes.find(x => x.id === c.conexaoId)?.banco ?? c.nome} (${fmt(c.saldo ?? 0)})`));
    // A leitura mais ANTIGA entre as contas: é ela que diz de quando é a soma.
    const lidoEm = contas.map(c => c.saldoEm).filter((q): q is string => !!q).sort()[0] ?? null;
    const ok = await confirmar({
      titulo: 'Igualar o saldo ao dos bancos?',
      alvo: fmt(soma),
      /*
       * Pedido dela (13/10): estava confuso. Frases curtas, uma ideia em cada,
       * e a comparação primeiro: o que o app mostra hoje contra o que há nos
       * bancos é o motivo de ela estar tocando aqui.
       */
      texto: (saldoDoApp !== null ? `Hoje o FinTracking mostra ${fmt(saldoDoApp)}. ` : '')
        + `Nos seus bancos há ${fmt(soma)}${contas.length > 1 ? `: ${deOnde}` : ''}. `
        + (lidoEm ? `Saldo lido ${quandoFoi(lidoEm)}. ` : '')
        + 'Ao confirmar, o saldo do FinTracking passa a ser o dos bancos, a partir de hoje. '
        + 'Os lançamentos antigos ficam no histórico e não mexem mais nele. '
        + 'Dinheiro em outro banco ou em espécie não entra.',
      acao: 'Igualar o saldo',
    });
    if (!ok) return;
    await comTrava(async () => {
      await onUsarComoSaldo(soma);
      return 'Saldo ajustado';
    });
  });

  /*
   * Em dois toques, e não abrindo a página direto: o endereço vem do servidor,
   * e uma janela aberta DEPOIS de uma espera o navegador bloqueia como pop-up.
   * O primeiro toque busca o link; o segundo é um link de verdade.
   */
  const pedirLinkDeConexao = () => fazer('conectar', async () => {
    setLinkDeConexao(await getLinkParaConectarBanco());
    // Os passos abrem no alto do corpo, e ela pode estar no fim da lista.
    setTimeout(() => caixa.current?.querySelector('.bco-corpo')?.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  });

  const gerarRetrato = () => fazer('retrato', async () => {
    setRetrato(JSON.stringify(await getRetratoDoCartao(), null, 1));
  });

  const enviarPedido = () => fazer('suporte', async () => {
    await enviarPedidoDeSuporte({
      conexaoId: pedido.conexaoId || (ativa?.id ?? dados.conexoes[0]?.id ?? ''),
      tipo: pedido.tipo,
      mensagem: pedido.mensagem.trim(),
      ...(pedido.valor.trim() ? { valor: pedido.valor.trim() } : {}),
    });
    setPedido(p => ({ ...p, mensagem: '', valor: '', enviado: true }));
    await carregarSuporte();
  });

  /**
   * Acerta a data das parceladas que foram lançadas um mês antes do que o
   * banco cobra. Muda lançamentos dela, então pergunta antes, dizendo quantas
   * e para onde vão.
   */
  const acertarParceladas = async (conexaoId: string, banco: string, quantas: number, meses: number) => {
    if (!await confirmar({
      titulo: 'Acertar a data dessas parceladas?',
      alvo: `${quantas} ${quantas === 1 ? 'parcelada' : 'parceladas'} do ${banco}`,
      texto: `Cada uma vai ${meses === 1 ? 'um mês' : `${meses} meses`} para a frente, que é quando o ${banco} cobra. `
        + 'Só a data muda: a compra, o valor e as parcelas continuam iguais.',
      acao: 'Acertar',
    })) return;
    await fazer('acertar', () => comTrava(async () => {
      const r = await acertarParceladasDoBanco(conexaoId);
      await carregar();
      onLancou();
      return r.acertadas > 0 ? 'Datas acertadas' : null;
    }));
  };

  /**
   * "Conectar meu banco": leva à tela do PROVEDOR, na mesma aba. É lá que ela
   * escolhe o banco e aceita o consentimento; a volta traz o id da sessão.
   * Na mesma aba porque uma janela aberta depois de uma espera o navegador
   * bloqueia como pop-up, e porque a volta precisa cair de novo no app.
   */
  const conectarMeuBanco = () => fazer('conectar-meu', async () => {
    const { url } = await iniciarConexaoDoBanco();
    window.location.assign(url);
  });

  /**
   * A volta do provedor: `?banco=voltou&cs=…&status=…`.
   *
   * "Voltou" não é "conectou": quem desiste ou esbarra num erro do banco
   * também volta. Só `connected` segue, e mesmo assim quem decide é o
   * servidor, que pergunta ao provedor de quem é a sessão. Os parâmetros saem
   * do endereço logo no começo, para um recarregar não repetir a confirmação.
   */
  const voltaTratada = useRef(false);
  useEffect(() => {
    if (voltaTratada.current) return;
    const p = new URLSearchParams(window.location.search);
    if (p.get('banco') !== 'voltou') return;
    voltaTratada.current = true;
    const sessao = p.get('cs') ?? '';
    const status = p.get('status') ?? '';
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    if (status !== 'connected' || !sessao) {
      setAviso(status === 'canceled'
        ? 'A conexão foi cancelada. Nada foi conectado.'
        : 'O banco não chegou a ser conectado. Você pode tentar de novo.');
      return;
    }
    void fazer('confirmar-conexao', async () => {
      const conexao = await confirmarConexaoDoBanco(sessao);
      await carregar();
      setBancoAtivo(conexao.id);
      // A primeira leitura, já: é ela que enche a caixa.
      const lido = await lerBancos([conexao]);
      await carregar();
      if (!dizerFalhas(lido.falhas)) setAviso(`${conexao.banco} conectado. ${resumo(lido)}`);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Ainda cabe banco no plano dela? Aí o "+ Banco" conecta. */
  const cabeMaisBanco = !!acesso && dados.conexoes.length < acesso.maxBancos;
  /** O banco extra é do plano Completo e se contrata em "Meu plano" (22/10).
   *  No teto do plano, nada a oferecer. */
  const extraPeloPlano = !!acesso && !!onVerPlano
    && acesso.maxBancos < (acesso.maxBancosNoTotal ?? acesso.maxBancos);

  /** Quem ainda não pode usar: sem plano com banco. */
  const semAcesso = acesso?.estado === 'sem';
  /** Liberada, mas não é a dona da chave: conecta o PRÓPRIO banco, e não busca no workspace. */
  const liberada = !!acesso && (acesso.estado === 'liberado' || acesso.estado === 'vencido');
  /**
   * Só a dona da chave busca e conecta pelo workspace do provedor. Enquanto a
   * tela não sabe quem é, NÃO mostra esses botões: mostrar por padrão foi o
   * que fez uma pessoa liberada tocar em "Buscar meus bancos" e receber "Não
   * disponível" (08/10).
   */
  const ehADona = acesso?.estado === 'dona';

  /** O diagnóstico é do banco da aba aberta: menos chamadas ao provedor, e é o banco de que ela está falando. */
  const bancoDoDiagnostico = (): string | undefined => (bancoAtivo || dados.conexoes[0]?.id) || undefined;

  /** Como o provedor chama cada conexão: é o que falta saber para ligar o banco de cada usuário. */
  const gerarFormato = () => fazer('formato', async () => {
    setRetrato(JSON.stringify(await getFormatoDasConexoes(), null, 1));
  });

  /** O mesmo quadro de texto serve aos dois: são relatórios para copiar. */
  const gerarRaioX = () => fazer('raio-x', async () => {
    setRetrato(JSON.stringify(await getRaioXDoBanco(undefined, bancoDoDiagnostico()), null, 1));
  });

  /** "Cadê o lançamento de R$ X?": onde a leitura pôs as linhas daquele valor. */
  const procurarValor = () => fazer('procurar', async () => {
    setRetrato(JSON.stringify(await getRaioXDoBanco(valorProcurado.trim(), bancoDoDiagnostico()), null, 1));
  });

  const copiarRetrato = async () => {
    try {
      await navigator.clipboard.writeText(retrato ?? '');
      setAviso('Retrato copiado.');
    } catch {
      setErro('Não consegui copiar. Selecione o texto e copie à mão.');
    }
  };

  /**
   * Lê os bancos pedidos — por padrão, só o da aba à vista.
   *
   * O botão mora dentro do bloco de UM banco, e lia todos: ela tocou em
   * "Atualizar" na aba do Itaú e perguntou se aquilo atualizava os três
   * (06/10). Atualizava, e não era o que o lugar do botão prometia. Agora ele
   * faz o que diz, e "atualizar todos" é um link ao lado, para quem quer.
   */
  const atualizar = (quais: { id: string; banco: string }[], releitura = false) => fazer('sincronizar', async () => {
    const r = await lerBancos(quais, releitura);
    await carregar();
    if (r.lancados) onLancou();
    // A leitura pode ter preenchido o limite e o fechamento de um cartão.
    onMetodosMudaram();
    // Se algum banco falhou e nada novo chegou, só o erro: "nada novo" ao
    // lado de "não consegui ler" seria a tela se contradizendo.
    if (!dizerFalhas(r.falhas) || r.novos > 0) setAviso(resumo(r));
  });

  /*
   * Ligar o automático pede confirmação, e a caixa diz as duas coisas que ela
   * precisa saber antes: o que NÃO entra sozinho (o duvidoso), e que vale
   * daqui pra frente. Desligar não pergunta.
   */
  const mudarModo = async (c: ConexaoDoBanco) => {
    const ligar = !c.automaticoDesde;
    if (ligar && !await confirmar({
      titulo: 'Lançar sozinho o que vier do banco?',
      alvo: c.banco,
      texto: 'O que chegar a partir de agora vira lançamento sem você tocar, quando eu souber a categoria. '
        + 'Continuo perguntando quando não souber, e quando parecer algo que você já lançou. '
        + 'O que já está esperando aqui continua esperando. Tudo que entrar sozinho aparece numa lista '
        + 'para você conferir, trocar a categoria ou desfazer. Vale só para este banco; os outros continuam como estão.',
      acao: 'Pode lançar',
    })) return;
    await fazer(`modo-${c.id}`, async () => {
      await definirModoDoBanco(c.id, ligar);
      await carregar();
    });
  };

  const ligarConta = (conta: ContaDoBanco) => fazer(`conta-${conta.id}`, async () => {
    const escolha = metodoDaConta[conta.id] ?? CADASTRAR;
    await ligarContaDoBanco(conta.id, escolha === CADASTRAR
      ? { nome: conta.nome }
      : { paymentMethodId: escolha });
    if (escolha === CADASTRAR) onMetodosMudaram();
    await carregar();
    onLancou();
  });

  const editar = (m: MovimentoDoBanco, mudanca: { categoryId?: string; nome?: string; aberto?: boolean }) =>
    setEdicoes(e => ({ ...e, [m.id]: { ...e[m.id], ...mudanca } }));

  /**
   * Roda um lançamento com a tela travada. `acao` devolve a frase do "feito",
   * ou `null` quando nada entrou — aí não há o que comemorar.
   *
   * O check fica `TEMPO_DO_FEITO` na tela: menos que isso e ele pisca sem ser
   * lido. Deu erro, a trava sai na hora e quem mostra o motivo é o `fazer`.
   */
  const comTrava = async (acao: () => Promise<string | null>) => {
    setTrava({ fase: 'lancando', texto: 'Lançando…' });
    try {
      const feito = await acao();
      if (feito) {
        setTrava({ fase: 'feito', texto: feito });
        await new Promise(r => setTimeout(r, TEMPO_DO_FEITO));
      }
    } finally {
      setTrava(null);
    }
  };

  const lancar = (m: MovimentoDoBanco) => fazer(m.id, () => comTrava(async () => {
    // Só vai o que ela mudou: campo ausente quer dizer "vale a sugestão".
    await aceitarMovimentoDoBanco(m.id, escolhaDe(m));
    await carregar();
    onLancou();
    return 'Lançado';
  }));

  const simples = (m: MovimentoDoBanco, acao: (id: string) => Promise<void>, recontar = false) =>
    fazer(m.id, async () => {
      await acao(m.id);
      await carregar();
      if (recontar) onLancou();
    });

  /**
   * Ignorar pergunta antes — e pode parar de perguntar.
   *
   * Ignorar não tem volta por esta tela: o movimento some e não reaparece nas
   * próximas leituras. Um toque sem aviso ao lado do "Lançar" era um erro
   * esperando para acontecer. Mas quem limpa vinte linhas de ruído não quer
   * vinte perguntas: "não perguntar de novo" fica guardado NESTE aparelho.
   */
  const ignorar = async (m: MovimentoDoBanco) => {
    let naoPerguntar = false;
    if (!ignoraSemPerguntar()) {
      const ok = await confirmar({
        titulo: 'Ignorar este movimento?',
        alvo: edicoes[m.id]?.nome?.trim() || m.sugestao.nome,
        texto: 'Ele sai desta lista e não vira lançamento. Não volta a aparecer nas próximas '
          + 'leituras do banco, e não dá para desfazer por aqui. Seus lançamentos não mudam.',
        acao: 'Ignorar',
        perigo: true,
        caixaDeSelecao: {
          rotulo: 'Não perguntar de novo',
          nota: 'Vale neste aparelho. Os próximos são ignorados com um toque.',
          aoResponder: marcado => { naoPerguntar = marcado; },
        },
      });
      if (!ok) return;
      if (naoPerguntar) lembrarDeNaoPerguntar();
    }
    await simples(m, ignorarMovimentoDoBanco);
  };

  /**
   * "É o pagamento da fatura deste cartão." Não cria lançamento: as compras
   * do cartão já contam. Se o valor cobre, a fatura fica paga; senão, o app
   * diz o que falta.
   */
  const vincularFatura = (m: MovimentoDoBanco, metodoId: string, mes: string, cartao: string) => fazer(m.id, async () => {
    const r = await vincularFaturaDoBanco(m.id, metodoId, mes);
    await carregar();
    onLancou();
    onMetodosMudaram();
    setAviso(r.paga
      ? `Fatura do ${cartao} de ${mesPorExtenso(mes)} paga. Nada foi lançado: as compras do cartão já contam.`
      : `Vinculado à fatura do ${cartao} de ${mesPorExtenso(mes)}. Pago ${fmt(r.pago)} de ${fmt(r.total)}: a fatura segue em aberto.`);
  });

  const desvincularFatura = (v: VinculoDeFatura) => fazer(v.id, async () => {
    await desvincularFaturaDoBanco(v.id);
    await carregar();
    onLancou();
    onMetodosMudaram();
  });

  /** O que vai no corpo do lançamento: só o que ela mudou. */
  const escolhaDe = (m: MovimentoDoBanco) => {
    const ed = edicoes[m.id] ?? {};
    return {
      ...(ed.categoryId !== undefined ? { categoryId: ed.categoryId || null } : {}),
      ...(ed.nome !== undefined && ed.nome.trim() ? { nome: ed.nome.trim() } : {}),
    };
  };

  const lancarEmLote = (alvos: MovimentoDoBanco[]) => fazer('lote', async () => {
    let r: { lancados: number; falhas: { id: string; erro: string }[] } = { lancados: 0, falhas: [] };
    await comTrava(async () => {
      /*
       * Em remessas. O servidor trabalha um tempo limitado por chamada e
       * devolve em `restantes` o que não deu para tentar; a tela reenvia até
       * acabar. Uma remessa que não lança nada encerra — senão um servidor
       * lento demais deixaria a tela travada rodando para sempre.
       */
      let fila = alvos.map(m => ({ id: m.id, ...escolhaDe(m) }));
      for (let volta = 0; volta < MAX_DE_REMESSAS && fila.length > 0; volta++) {
        const parte = await aceitarMovimentosDoBancoEmLote(fila.slice(0, POR_REMESSA));
        r.lancados += parte.lancados;
        r.falhas.push(...parte.falhas);
        const sobrou = new Set([...(parte.restantes ?? []), ...fila.slice(POR_REMESSA).map(i => i.id)]);
        if (parte.lancados === 0 && (parte.restantes ?? []).length > 0) {
          r.falhas.push(...Array.from(sobrou).map(id => ({ id, erro: 'O servidor está lento. Tente de novo em instantes.' })));
          break;
        }
        fila = fila.filter(i => sobrou.has(i.id));
        if (r.lancados > 0) setTrava({ fase: 'lancando', texto: `Lançando… ${r.lancados} de ${alvos.length}` });
      }
      await carregar();
      if (r.lancados > 0) onLancou();
      if (r.lancados === 0) return null;
      return r.lancados === 1 ? '1 lançado' : `${r.lancados} lançados`;
    });
    // Sai da seleção o que entrou. O que falhou continua marcado, à vista.
    const falhou = new Set(r.falhas.map(f => f.id));
    setMarcados(new Set(alvos.filter(m => falhou.has(m.id)).map(m => m.id)));
    if (r.lancados > 0) setAviso(r.lancados === 1 ? '1 movimento lançado.' : `${r.lancados} movimentos lançados.`);
    if (r.falhas.length > 0) {
      setErro(`${r.falhas.length === 1 ? '1 não entrou' : `${r.falhas.length} não entraram`}: ${r.falhas[0].erro}`);
    }
    // O resultado é escrito no alto da tela, e ela está no pé da lista.
    setTimeout(() => caixa.current?.querySelector('.bco-erro, .bco-aviso')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
  });

  const dispensarEstorno = (e: EstornadoDoBanco) => fazer(e.id, async () => {
    await dispensarEstornoDoBanco(e.id);
    await carregar();
  });

  /**
   * O banco estornou uma compra que ela JÁ tinha lançado.
   *
   * Apagar o lançamento é dela decidir: o banco mudou de ideia, mas quem sabe
   * se aquilo ainda faz sentido nas contas dela é ela (um estorno parcial, um
   * crédito que vai virar outra compra). Por isso pergunta, e "manter" existe.
   */
  const apagarLancamentoEstornado = async (e: EstornadoDoBanco) => {
    if (!e.lancadoComo) return;
    const ok = await confirmar({
      titulo: 'Apagar o lançamento estornado?',
      alvo: e.descricao,
      texto: `O banco estornou esta compra de ${fmt(e.valor)}. O lançamento que você fez dela é apagado, `
        + (e.parcela ? 'com as parcelas que ainda iam cair. ' : '')
        + 'Saldo e projeção recontam sem ele.',
      acao: 'Apagar lançamento',
      perigo: true,
    });
    if (!ok) return;
    await fazer(e.id, async () => {
      await desfazerMovimentoDoBanco(e.lancadoComo as string);
      await dispensarEstornoDoBanco(e.id);
      await carregar();
      onLancou();
    });
  };

  const trocarCategoria = (m: MovimentoDoBanco, categoryId: string) => fazer(m.id, async () => {
    await corrigirMovimentoDoBanco(m.id, { categoryId: categoryId || null });
    await carregar();
    onLancou();
  });

  const desconectar = async (c: ConexaoDoBanco) => {
    if (!await confirmar({
      titulo: 'Desconectar este banco?',
      alvo: c.banco,
      texto: 'O FinTracking para de ler o extrato e a autorização é revogada. Os lançamentos que já '
        + 'entraram continuam; o que ainda estava esperando aqui some.',
      acao: 'Desconectar', perigo: true,
    })) return;
    await fazer('desconectar', async () => {
      await desconectarBanco(c.id);
      await carregar();
    });
  };

  const nomeDaCategoria = (id: string | null | undefined) => categories.find(c => c.id === id)?.name ?? null;
  const lancamento = (id: string | null) => transactions.find(t => t.id === id);

  /*
   * Uma aba por banco, quando há mais de um (pedido dela, 06/10).
   *
   * Cada banco tem o próprio modo, as próprias contas e a própria fila. Numa
   * lista só, o "Lançar sozinho" do Itaú e o do Nubank ficariam empilhados no
   * topo, e não daria para saber de qual banco é cada movimento esperando.
   *
   * Com um banco só não há aba: seria uma aba para escolher entre uma opção.
   *
   * Se o banco da aba ativa sumir (ela desconectou), cai no primeiro que
   * sobrou, em vez de mostrar uma tela vazia de um banco que não existe mais.
   */
  const comAbas = dados.conexoes.length > 1;
  const ativa = dados.conexoes.find(c => c.id === bancoAtivo) ?? dados.conexoes[0];
  const doBanco = <T extends { conexaoId: string }>(itens: T[]): T[] =>
    (comAbas && ativa ? itens.filter(i => i.conexaoId === ativa.id) : itens);
  const visto = {
    conexoes: comAbas && ativa ? [ativa] : dados.conexoes,
    contas: doBanco(dados.contas),
    pendentes: doBanco(dados.pendentes),
    casados: doBanco(dados.casados),
    lancados: doBanco(dados.lancados),
  };
  /** Quanto cada banco pede dela: é o número na aba. */
  const pedindo = (conexaoId: string): number =>
    dados.pendentes.filter(m => m.conexaoId === conexaoId).length
    + (dados.estornados ?? []).filter(e => e.conexaoId === conexaoId).length
    + dados.contas.filter(c => c.conexaoId === conexaoId && !c.paymentMethodId).length;

  const semMetodo = visto.contas.filter(c => !c.paymentMethodId);
  /*
   * O que parece pagamento de fatura sai das outras listas: ali ele teria
   * "Lançar", e lançar a fatura é contar o cartão duas vezes. Volta a ser um
   * movimento comum se ela disser que não é.
   */
  const ehFatura = (m: MovimentoDoBanco) => seguraOLancamento(m.fatura) && !naoEFatura.has(m.id);
  const deFatura = visto.pendentes.filter(ehFatura);
  const semFatura = visto.pendentes.filter(m => !ehFatura(m));
  const provaveis = semFatura.filter(m => m.estado === 'provavel');
  const semCategoria = semFatura.filter(m => m.estado === 'novo' && !m.sugestao.sabe);
  const prontos = semFatura.filter(m => m.estado === 'novo' && m.sugestao.sabe);
  const cartoesDeFatura = dados.cartoesDeFatura ?? [];
  const faturasVinculadas = dados.faturas ?? [];
  const estornados = (dados.estornados ?? []).filter(e => visto.conexoes.some(c => c.id === e.conexaoId));
  // Só as do banco da aba: o botão acerta um banco de cada vez.
  const adiantadas = (dados.adiantadas ?? []).filter(p => p.conexaoId === visto.conexoes[0]?.id);
  /**
   * A soma do que o banco diz haver nas contas correntes — de TODOS os bancos
   * conectados, não só o da aba: o saldo do app é um só. `null` = nenhum saldo.
   */
  /**
   * O que ainda espera por ela em TODOS os bancos: movimentos e contas sem
   * método. É o que segura a oferta de acertar o saldo — ver onde é usada.
   */
  const faltaResolver = dados.pendentes.length + dados.contas.filter(c => !c.paymentMethodId).length;
  const contasComSaldo = dados.contas.filter(c => c.tipo === 'BANK' && typeof c.saldo === 'number');
  const saldoNoBanco = contasComSaldo.length === 0
    ? null
    : Math.round(contasComSaldo.reduce((s, c) => s + (c.saldo ?? 0), 0) * 100) / 100;
  /** Os saldos discordam, mas ainda há o que lançar: a oferta espera, e o "i" do saldo diz por quê. */
  const esperaPendencias = saldoNoBanco !== null && saldoDoApp !== null
    && Math.abs(saldoNoBanco - saldoDoApp) >= 0.01 && faltaResolver > 0;
  /**
   * O que pode entrar num lote: tem método confirmado e não está com a edição
   * aberta. Rascunho não confirmado não é lançado — nem de um em um, nem aqui.
   */
  const marcaveis = prontos.filter(m => !!m.metodo && !rascunhos[m.id]);
  /** Só os da aba à vista: o que ela marcou em outro banco não entra junto. */
  const selecionados = marcaveis.filter(m => marcados.has(m.id));

  const topo = (m: MovimentoDoBanco) => (
    <>
      <div className="bco-linha-topo">
        <span className="bco-data">{dataNoCartao(m)}{m.metodo && <> · {m.metodo}</>}</span>
        <span className={`bco-valor ${m.sentido}`}>{m.sentido === 'entrada' ? '+' : '−'}{fmt(m.valor)}</span>
      </div>
      <p className="bco-desc">
        {edicoes[m.id]?.nome?.trim() || m.sugestao.nome}
        {m.parcela && <em className="bco-parcela"> {m.parcela.numero}/{m.parcela.total}</em>}
      </p>
      {/*
        Parcelada que chega no meio: a data acima é a da COMPRA, de meses
        atrás, e sem esta linha "8/10" com data de janeiro parece engano.
      */}
      {/*
        Compra ainda pendente no banco. Entra na lista porque alguns bancos a
        deixam assim até a fatura fechar; o aviso existe porque ela ainda pode
        mudar de valor ou ser cancelada, e é justo ela saber antes de lançar.
      */}
      {m.pendente && (
        <p className="bco-andamento">
          <span className="bco-etiqueta confira">pendente no banco</span>{' '}
          O banco ainda não confirmou. Se o valor mudar ou a compra for cancelada, eu aviso.
        </p>
      )}
      {m.parcela && m.parcela.numero > 1 && (
        <p className="bco-andamento">
          Parcela {m.parcela.numero} de {m.parcela.total}
          {m.mesDaFatura ? `, paga na fatura de ${m.mesDaFatura.slice(5, 7)}/${m.mesDaFatura.slice(0, 4)}` : ''}.
          {' '}As seguintes o FinTracking projeta. Compra de {dataCurta(m.data)}.
        </p>
      )}
    </>
  );

  /**
   * A data que o cartão mostra numa parcelada em andamento: a da FATURA.
   *
   * Pedido dela: "o que importa é que em setembro vou pagar a parcela x". A
   * data da compra, de meses atrás, é o que o banco manda na linha, mas não é
   * quando o dinheiro sai — e é na fatura que o lançamento entra. A compra
   * fica dita embaixo, em letra pequena.
   */
  const dataNoCartao = (m: MovimentoDoBanco): string => {
    if (!m.parcela || m.parcela.numero <= 1 || !m.mesDaFatura) return dataCurta(m.data);
    const dia = dados.contas.find(c => c.id === m.contaId)?.diaDeVencimento;
    return dia ? `${String(dia).padStart(2, '0')}/${m.mesDaFatura.slice(5, 7)}` : `fatura de ${m.mesDaFatura.slice(5, 7)}/${m.mesDaFatura.slice(2, 4)}`;
  };

  const criarCategoria = (m: MovimentoDoBanco, aoMudar: (id: string) => void) => {
    const nome = nova?.nome.trim();
    if (!nome) return;
    // Já existe com esse nome: usa a que existe. O servidor recusaria a
    // duplicata, e para ela o resultado certo é o mesmo — a categoria escolhida.
    const igual = categories.find(c => c.name.trim().toLowerCase() === nome.toLowerCase());
    if (igual) { aoMudar(igual.id); setNova(null); return; }
    /*
     * Estado PRÓPRIO, e não o `ocupado` do movimento: com ele, criar a
     * categoria deixava o botão ao lado em "Lançando…", como se o movimento
     * já estivesse entrando. E o erro aparece aqui, ao lado do campo — o
     * aviso geral fica no topo da tela, fora da vista de quem rolou a lista.
     */
    setNova({ de: m.id, nome, criando: true });
    (async () => {
      try {
        const cor = CORES_DE_CATEGORIA[categories.length % CORES_DE_CATEGORIA.length];
        const criada = await createCategory({ name: nome, color: cor });
        // Nome e cor são os que ela acabou de escolher: o seletor não depende
        // de a resposta repeti-los para mostrar a opção com o nome certo.
        setCriadas(antes => [...antes, { ...criada, name: criada.name ?? nome, color: criada.color ?? cor }]);
        onCategoriasMudaram();
        setNova(null);
        aoMudar(criada.id);
      } catch (e) {
        setNova({ de: m.id, nome, erro: mensagemDeErro(e, 'Não consegui criar a categoria.') });
      }
    })();
  };

  const seletorDeCategoria = (m: MovimentoDoBanco, valor: string, aoMudar: (id: string) => void) => (
    nova?.de === m.id ? (
      <>
        <input
          autoFocus
          maxLength={LIMITES.nome}
          aria-label="Nome da nova categoria"
          placeholder="Nome da nova categoria"
          value={nova.nome}
          disabled={!!nova.criando}
          onChange={e => setNova({ de: m.id, nome: e.target.value })}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); criarCategoria(m, aoMudar); }
            // Só o campo: sem parar aqui, o Esc fecharia a tela inteira.
            if (e.key === 'Escape') { e.stopPropagation(); setNova(null); }
          }}
        />
        <button className="bco-btn principal" onClick={() => criarCategoria(m, aoMudar)}
          disabled={!nova.nome.trim() || !!nova.criando} aria-busy={!!nova.criando}>
          Criar
        </button>
        <button className="bco-btn" onClick={() => setNova(null)} disabled={!!nova.criando}>Cancelar</button>
        {nova.erro && <p className="bco-erro bco-erro-da-categoria" role="alert">{nova.erro}</p>}
      </>
    ) : (
      <select
        value={valor}
        aria-label={`Categoria de ${m.sugestao.nome}`}
        onChange={e => (e.target.value === NOVA_CATEGORIA
          ? setNova({ de: m.id, nome: '' })
          : aoMudar(e.target.value))}
      >
        <option value="">Sem categoria</option>
        {/*
          As criadas agora vêm PRIMEIRO, em todo seletor da tela. Ela criou uma
          categoria num movimento e não a achou nos de baixo: a lista do app
          volta em ordem alfabética, e a nova ia parar no meio de catorze.
        */}
        {criadas.length > 0 && (
          <optgroup label="Criadas agora">
            {criadas.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </optgroup>
        )}
        {categories.filter(c => !criadas.some(n => n.id === c.id))
          .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        <option value={NOVA_CATEGORIA}>+ Criar categoria…</option>
      </select>
    )
  );

  /** Lançar fica travado enquanto a conta do banco não tiver método. */
  /**
   * Sem método confirmado na conta do banco, nada dela é lançado. O servidor
   * também recusa (`lancar`, em `banco/repo.ts`); aqui é a tela DIZENDO.
   *
   * Antes o botão só ficava apagado, com o motivo numa dica de mouse — que no
   * celular não existe. Ela leu o botão apagado como "está lançando".
   */
  const irParaOMetodo = () =>
    caixa.current?.querySelector('#bco-metodo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  /** O seletor de cartão e mês, para quando o app não sabe (ou errou) qual fatura é. */
  const escolherFatura = (m: MovimentoDoBanco) => {
    const e = escolhaDeFatura[m.id];
    if (!e) return null;
    const mudar = (mudanca: Partial<{ metodoId: string; mes: string }>) =>
      setEscolhaDeFatura(x => ({ ...x, [m.id]: { ...x[m.id], ...mudanca } }));
    const cartao = cartoesDeFatura.find(c => c.id === e.metodoId);
    return (
      <>
        <div className="bco-escolha">
          <select value={e.metodoId} onChange={ev => mudar({ metodoId: ev.target.value })} aria-label="Cartão da fatura">
            {cartoesDeFatura.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <select value={e.mes} onChange={ev => mudar({ mes: ev.target.value })} aria-label="Mês da fatura">
            {mesesParaEscolher(m.data).map(mes => (
              <option key={mes} value={mes}>fatura de {mesPorExtenso(mes)}</option>
            ))}
          </select>
        </div>
        <div className="bco-acoes">
          <button
            className="bco-btn principal"
            disabled={ocupado === m.id || !cartao}
            onClick={() => cartao && vincularFatura(m, cartao.id, e.mes, cartao.nome)}
          >
            É esta fatura
          </button>
          <button
            className="bco-btn"
            onClick={() => setEscolhaDeFatura(x => Object.fromEntries(Object.entries(x).filter(([id]) => id !== m.id)))}
          >
            Cancelar
          </button>
        </div>
      </>
    );
  };

  const abrirEscolhaDeFatura = (m: MovimentoDoBanco) => {
    const o = m.fatura?.opcoes[0];
    setEscolhaDeFatura(x => ({
      ...x,
      [m.id]: { metodoId: o?.metodoId ?? cartoesDeFatura[0]?.id ?? '', mes: o?.mes ?? m.data.slice(0, 7) },
    }));
  };

  /**
   * No movimento COMUM: a porta para dizer "isto é pagamento de fatura".
   *
   * Aparece só na saída da conta, e só para quem tem cartão com fatura. Com um
   * palpite (a descrição cita o banco de um cartão dela), diz qual; sem
   * palpite, é só o link.
   */
  const portaDaFatura = (m: MovimentoDoBanco) => {
    if (m.conta !== 'BANK' || m.sentido !== 'saida' || cartoesDeFatura.length === 0) return null;
    if (escolhaDeFatura[m.id]) return escolherFatura(m);
    const o = opcaoSugerida(m.fatura);
    return (
      <p className="bco-fatura-porta">
        {o ? <>Pode ser a fatura do {o.nome} de {mesPorExtenso(o.mes)}. </> : null}
        <button className="bco-link" onClick={() => abrirEscolhaDeFatura(m)}>é pagamento de fatura?</button>
      </p>
    );
  };

  const botaoLancar = (m: MovimentoDoBanco) => (
    <>
      <button
        className="bco-btn principal"
        onClick={() => lancar(m)}
        disabled={ocupado === m.id || !m.metodo}
      >
        Lançar
      </button>
      {!m.metodo && (
        <button className="bco-falta-metodo" onClick={irParaOMetodo}>
          Para lançar, confirme antes o método de pagamento desta conta ↑
        </button>
      )}
    </>
  );

  return createPortal(
    <div className="bco-overlay" onClick={trava ? undefined : onClose} role="presentation" ref={caixa}>
      <div className="bco-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="bco-titulo">
        <div className="bco-banda">
          <h2 id="bco-titulo">Open Finance</h2>
          {/* A liberada: "+ Banco" conecta quando ainda cabe no plano; sem
              caber, leva a "Meu plano". No teto, o botão some. */}
          {!ajuda && !desligado && liberada && dados.conexoes.length > 0 && (cabeMaisBanco || extraPeloPlano) && (
            <button
              className="bco-como" disabled={!!ocupado}
              onClick={() => (cabeMaisBanco ? conectarMeuBanco() : onVerPlano!())}
              aria-label="Conectar outro banco" title="Conectar outro banco"
            >
              + Banco
            </button>
          )}
          {!ajuda && !desligado && ehADona && dados.conexoes.length > 0 && (
            <button className="bco-como" onClick={pedirLinkDeConexao} disabled={!!ocupado}
              aria-label="Conectar outro banco" title="Conectar outro banco">
              + Banco
            </button>
          )}
          {!ajuda && (
            <button className="bco-como" onClick={() => setAjuda(true)} aria-label="Como funciona">
              {/* No celular o cabeçalho tem dois botões: o rótulo encurta para
                  o título "Open Finance" não ser cortado. */}
              <span className="bco-so-largo">Como funciona</span>
              <span className="bco-so-estreito">Ajuda</span>
            </button>
          )}
          <button className="bco-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="bco-corpo">
          {ajuda ? <ComoFuncionaOpenFinance onVoltar={() => setAjuda(false)} /> : carregando ? (
            /* Um esqueleto do que vem, e não uma palavra solta no vazio. */
            <div className="bco-esqueleto" role="status" aria-label="Carregando o que veio do banco">
              <span className="skeleton-line bco-esq-abas" />
              <span className="skeleton-line bco-esq-linha" />
              <span className="skeleton-line bco-esq-item" />
              <span className="skeleton-line bco-esq-item" />
              <span className="skeleton-line bco-esq-item" />
            </div>
          ) : semAcesso && acesso ? (
            <OfertaDoOpenFinance acesso={acesso} onComoFunciona={() => setAjuda(true)} onVerPlano={onVerPlano} />
          ) : desligado ? (
            <div className="bco-vazio">
              {/* Duas situações, duas frases curtas. O parágrafo com as
                  causas possíveis saiu: era texto de quem cuida do servidor,
                  na tela de quem só quer ver os lançamentos (pedido dela). */}
              <h3 className="bco-abre-titulo">
                {desligado === 'iniciando' ? 'O servidor está reiniciando' : 'O Open Finance está desligado para esta conta'}
              </h3>
              <p className="bco-abre-sub">
                {desligado === 'iniciando'
                  ? 'Leva alguns segundos. Seus bancos e lançamentos continuam guardados.'
                  : 'Nada foi apagado: seus bancos conectados, os lançamentos e o que esperava na lista continuam guardados.'}
              </p>
              <div className="bco-acoes">
                <button className="bco-btn principal" onClick={() => { setCarregando(true); void carregar(); }}>
                  Tentar de novo
                </button>
              </div>
            </div>
          ) : (
            <>
              {dados.conexoes.length === 0 ? (
                <div className="bco-vazio">
                  <h3 className="bco-abre-titulo">Seu banco conectado, seus gastos em dia</h3>
                  <p className="bco-abre-sub">
                    O FinTracking lê o extrato todo dia e lança por você.
                  </p>
                  <ul className="bco-abre-lista">
                    <li>
                      <strong>Método de pagamento</strong>
                      <span>É o da conta do banco. Você confirma uma vez.</span>
                    </li>
                    <li>
                      <strong>Categoria</strong>
                      <span>Ele descobre sozinho. Quando não sabe, pergunta e lembra.</span>
                    </li>
                    <li>
                      <strong>Você no controle</strong>
                      <span>Escolha conferir antes ou deixar entrar sozinho.</span>
                    </li>
                    <li>
                      <strong>Atualizado todo dia</strong>
                      <span>
                        Lemos o extrato todo dia às 6h, e você pode atualizar na hora quando
                        quiser.
                      </span>
                    </li>
                  </ul>
                  <p className="bco-abre-seguro">
                    Leitura apenas: daqui não sai Pix nem pagamento.
                  </p>
                  {/* "Regulado", e não "garantido": o Banco Central regula o
                      Open Finance, não o FinTracking. A frase só afirma o que
                      é verdade — e o que ela pode conferir no site do banco. */}
                  <p className="bco-abre-bacen">
                    O Open Finance é regulado pelo Banco Central. A autorização é dada no
                    site do seu banco, e você pode cancelar quando quiser.
                  </p>
                  {/* Dito ANTES de conectar: descobrir depois que o cartão
                      adicional não vem é descobrir tarde. */}
                  <p className="bco-abre-bacen">
                    Só vem o que está no seu nome. Cartão adicional de outra pessoa fica de fora.
                  </p>
                  {liberada ? (
                    /* A liberada conecta o PRÓPRIO banco, numa sessão só dela.
                       "Buscar meus bancos" olha o workspace do provedor inteiro
                       e é só da dona da chave — o servidor também recusa. */
                    <p className="bco-abre-passo">
                      Seu acesso está liberado{acesso?.liberadoAte ? ` até ${dataCurta(acesso.liberadoAte)}` : ''}. Ao
                      tocar em conectar você vai para a tela do Banco MCP, que opera a conexão: é lá
                      que você escolhe o banco e autoriza a leitura. O FinTracking nunca vê a sua senha.
                    </p>
                  ) : ehADona ? (
                    <p className="bco-abre-passo">
                      Conecte o banco na sua conta do Banco MCP e depois busque aqui.
                    </p>
                  ) : null}
                  <div className="bco-acoes">
                    {liberada ? (
                      <button className="bco-btn principal" onClick={conectarMeuBanco} disabled={!!ocupado}>
                        Conectar meu banco
                      </button>
                    ) : ehADona ? (
                      <button className="bco-btn principal" onClick={buscarBancos} disabled={!!ocupado}>
                        Buscar meus bancos
                      </button>
                    ) : null}
                    {/* Antes de conectar é quando a explicação mais importa. */}
                    <button className="bco-btn" onClick={() => setAjuda(true)}>Como funciona</button>
                  </div>
                </div>
              ) : (
                <div className="bco-conexoes">
                  {/*
                    Os passos de conexão, quando ela pede pelo "+ Banco" do
                    cabeçalho. O botão morava aqui no corpo, ocupando uma linha
                    inteira em toda visita para uma ação que se faz duas ou
                    três vezes na vida (pedido dela, 06/10).
                  */}
                  {linkDeConexao && (
                    <div className="bco-conectar">
                      <ol>
                        <li>
                          <a href={linkDeConexao} target="_blank" rel="noopener noreferrer">
                            Abra a página de conexão ↗
                          </a>{' '}
                          e escolha o banco.
                        </li>
                        <li>Autorize no site do banco. É lá que você dá o consentimento.</li>
                        <li>Volte aqui e toque no botão abaixo.</li>
                      </ol>
                      <p className="bco-conectar-nota">
                        O banco libera só o que está no seu nome: cartão adicional de outra pessoa
                        não aparece.
                      </p>
                      <div className="bco-acoes">
                        <button className="bco-btn principal" onClick={buscarBancos} disabled={!!ocupado}>
                          Já conectei, buscar
                        </button>
                        <button className="bco-btn" onClick={() => setLinkDeConexao(null)}>Fechar</button>
                      </div>
                    </div>
                  )}
                  {comAbas && (
                    <div className="bco-abas" role="tablist" aria-label="Bancos conectados">
                      {dados.conexoes.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          role="tab"
                          aria-selected={c.id === ativa?.id}
                          className={`bco-aba${c.id === ativa?.id ? ' ativa' : ''}`}
                          onClick={() => {
                            setBancoAtivo(c.id);
                            // O recado de um banco não acompanha a troca de aba:
                            // "não consegui ler o Itaú" na aba do Santander parecia
                            // ser do Santander.
                            setErro(''); setAviso('');
                          }}
                        >
                          {c.banco}
                          {pedindo(c.id) > 0 && <span className="bco-conta">{pedindo(c.id)}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {visto.conexoes.map(c => {
                    const e = ESTADO_DA_CONEXAO[c.status] ?? { texto: c.status.toLowerCase(), problema: false };
                    return (
                      <div className="bco-conexao-bloco" key={c.id}>
                        {/* De quando é o que está na tela. Sem isto ela não tem
                            como saber se falta um gasto ou se ele é de antes.
                            Atrás do "i" desde 22/10, ao lado do estado. */}
                        <ComInfo
                          rotulo="De quando é o extrato"
                          texto={c.periodo
                            ? <>Extrato lido de {dataCurta(c.periodo.desde)} a {dataCurta(c.periodo.ate)}.</>
                            : 'O extrato ainda não foi lido.'}
                          linha={botao => (
                            <div className="bco-conexao">
                              <strong>{c.banco}</strong>
                              <span className={`bco-estado${e.problema ? ' problema' : ''}`}>{e.texto}{botao}</span>
                              <button className="bco-link" onClick={() => desconectar(c)} disabled={!!ocupado}>
                                desconectar
                              </button>
                            </div>
                          )}
                        />
                        {/* O saldo que o BANCO diz, com a hora: é um retrato da
                            última leitura, não um número ao vivo. */}
                        {visto.contas.filter(k => k.conexaoId === c.id && typeof k.saldo === 'number').map(k => (
                          esperaPendencias ? (
                            /* Por que o saldo ainda não é igualado: atrás do "i" (22/10). */
                            <ComInfo
                              key={k.id}
                              rotulo="Quando o saldo é igualado"
                              texto={<>
                                O saldo do FinTracking vai mudando enquanto você lança. Quando não houver mais
                                nada esperando aqui ({faltaResolver} {faltaResolver === 1 ? 'pendência' : 'pendências'}),
                                ofereço igualar o saldo ao dos seus bancos.
                              </>}
                              linha={botao => (
                                <p className="bco-saldo">
                                  Saldo no banco: <strong>{fmt(k.saldo as number)}</strong>
                                  {k.saldoEm && <span> · lido {quandoFoi(k.saldoEm)}</span>}
                                  {botao}
                                </p>
                              )}
                            />
                          ) : (
                            <p className="bco-saldo" key={k.id}>
                              Saldo no banco: <strong>{fmt(k.saldo as number)}</strong>
                              {k.saldoEm && <span> · lido {quandoFoi(k.saldoEm)}</span>}
                            </p>
                          )
                        ))}
                        {/*
                          O modo, dito pelo que ele FAZ. "Automático: ligado" não
                          diz a ninguém o que muda; "Lançar sozinho" diz.
                        */}
                        <label className="bco-modo">
                          <input
                            type="checkbox"
                            checked={!!c.automaticoDesde}
                            onChange={() => mudarModo(c)}
                            disabled={ocupado === `modo-${c.id}`}
                          />
                          <span>
                            {/* Com o nome do banco: o modo é POR banco, e sem o nome
                                a dúvida era se valia para todos. */}
                            <strong>Lançar sozinho no {c.banco}</strong>
                            <em>
                              {c.automaticoDesde
                                ? 'O que chega já vira lançamento. Eu pergunto só quando não sei.'
                                : 'Hoje eu aviso e você confere antes. Marque para eu lançar por você.'}
                            </em>
                          </span>
                        </label>
                      </div>
                    );
                  })}
                  {visto.conexoes.some(c => ESTADO_DA_CONEXAO[c.status]?.problema) && (
                    <p className="bco-alerta" role="status">
                      Um banco precisa de você para voltar a ser lido. Reconecte na sua conta do
                      Banco MCP e toque em “Atualizar”.
                    </p>
                  )}
                  {/*
                    Só quando os dois números existem e discordam. Igual ao do
                    app, não há o que oferecer; sem o do app, não há com o que
                    comparar.
                  */}
                  {saldoNoBanco !== null && saldoDoApp !== null && Math.abs(saldoNoBanco - saldoDoApp) >= 0.01 && (
                    /*
                     * Acertar o saldo é o ÚLTIMO passo, e a tela segura a
                     * oferta até lá. Pedido dela: cada lançamento mexe no
                     * saldo, e acertar no meio do caminho é acertar um número
                     * que o próximo "Lançar" muda de novo. Com a lista vazia,
                     * o que o app sabe e o que o banco sabe são a mesma
                     * história, e a diferença que sobrar é a de verdade.
                     * Enquanto há pendência, o aviso fica no "i" do saldo no
                     * banco (22/10): ver `esperaPendencias`.
                     */
                    faltaResolver > 0 ? null : (
                      <div className="bco-saldo-difere">
                        {/*
                          Dois números lado a lado, a conta banco a banco numa
                          linha, e o botão embaixo, no centro. Antes era uma
                          frase só ao lado do botão: com três bancos ela dava
                          sete linhas espremidas, e o saldo negativo quebrava
                          entre o sinal e o valor (pedido dela, 06/10).
                        */}
                        <div className="bco-saldo-par">
                          <div className="bco-saldo-caixa">
                            <span className="bco-saldo-rotulo">Nos seus bancos</span>
                            <strong className="bco-saldo-numero">{fmt(saldoNoBanco)}</strong>
                          </div>
                          <div className="bco-saldo-caixa">
                            <span className="bco-saldo-rotulo">No FinTracking</span>
                            <strong className="bco-saldo-numero">{fmt(saldoDoApp)}</strong>
                          </div>
                        </div>
                        {contasComSaldo.length > 1 && (
                          <p className="bco-saldo-detalhe">
                            {contasComSaldo.map(c =>
                              `${dados.conexoes.find(x => x.id === c.conexaoId)?.banco ?? c.nome} ${fmt(c.saldo ?? 0)}`).join(' · ')}
                          </p>
                        )}
                        <p className="bco-saldo-detalhe">
                          O app calcula pelos lançamentos; o banco sabe quanto há de fato.
                        </p>
                        <button className="bco-btn principal" onClick={usarSaldoDoBanco} disabled={!!ocupado}>
                          Igualar ao saldo dos bancos
                        </button>
                      </div>
                    )
                  )}
                  {/* É aqui que bate a dúvida "por que não está igual ao app do
                      banco?": a resposta fica no "i" ao lado do botão que
                      resolve (22/10; antes era um parágrafo sempre aberto). */}
                  <ComInfo
                    rotulo="Quando o extrato é lido"
                    texto={<>
                      Lemos o extrato todo dia às 6h. Na conta corrente, o que ainda está pendente no
                      banco entra quando ele confirmar. No cartão, a compra pendente já aparece.
                    </>}
                    linha={botao => (
                      <div className="bco-atualizar-linha">
                        <button className="bco-btn" onClick={() => atualizar(visto.conexoes)} disabled={!!ocupado}>
                          {comAbas && ativa ? `Atualizar o ${ativa.banco}` : 'Atualizar'}
                        </button>
                        {botao}
                      </div>
                    )}
                  />
                  {/* "Reler os últimos 60 dias" saiu daqui em 08/10: para quem
                      usa não quer dizer nada, e o Atualizar já traz o que
                      falta. Ficou com as ferramentas de diagnóstico, lá embaixo. */}
                  {comAbas && (
                    <p className="bco-atualizar-todos">
                      <button className="bco-link" onClick={() => atualizar(dados.conexoes)} disabled={!!ocupado}>
                        atualizar todos os bancos
                      </button>
                    </p>
                  )}
                </div>
              )}

              {/* Toda mensagem tem um ✕ (08/10): "o banco não respondeu" ficava
                  na tela até a próxima ação, muito depois de ela já ter lido. */}
              {erro && (
                <p className="bco-erro" role="alert">
                  <span>{erro}</span>
                  <button type="button" className="bco-dispensar" onClick={() => setErro('')} aria-label="Fechar esta mensagem">✕</button>
                </p>
              )}
              {aviso && (
                <p className="bco-aviso" role="status">
                  <span>{aviso}</span>
                  <button type="button" className="bco-dispensar" onClick={() => setAviso('')} aria-label="Fechar esta mensagem">✕</button>
                </p>
              )}

              {adiantadas.length > 0 && (
                <section>
                  <h3 className="bco-subtitulo">Parceladas no mês errado <span className="bco-conta">{adiantadas.length}</span></h3>
                  <p className="bco-nota">
                    O {visto.conexoes[0]?.banco} cobra estas parcelas {adiantadas[0].meses === 1 ? 'um mês' : `${adiantadas[0].meses} meses`} depois
                    do que foi lançado.
                  </p>
                  {adiantadas.map(p => (
                    <div className="bco-item" key={p.id}>
                      <div className="bco-linha-topo">
                        <span className="bco-data">{p.parcela}</span>
                        <span className="bco-valor">{fmt(p.valor)}</span>
                      </div>
                      <p className="bco-desc">{p.descricao}</p>
                      <p className="bco-andamento">
                        {p.noAppEm && p.noBancoEm
                          ? <>Parcela {p.parcela}: no app está em {nomeDoMes(p.noAppEm)}. O banco cobra em {nomeDoMes(p.noBancoEm)}.</>
                          : <>Lançada em {dataCurta(p.lancadoEm)}. O certo é {dataCurta(p.certoEm)}.</>}
                      </p>
                    </div>
                  ))}
                  <button
                    type="button" className="bco-btn principal" disabled={!!ocupado}
                    onClick={() => acertarParceladas(adiantadas[0].conexaoId, visto.conexoes[0]?.banco ?? 'banco', adiantadas.length, adiantadas[0].meses)}
                  >
                    Acertar as datas
                  </button>
                </section>
              )}

              {estornados.length > 0 && (
                <section>
                  <h3 className="bco-subtitulo">Estornados pelo banco <span className="bco-conta">{estornados.length}</span></h3>
                  <p className="bco-nota">Compras que o banco desfez. Não entram nos seus lançamentos.</p>
                  {estornados.map(e => (
                    <div className="bco-item estornado" key={e.id}>
                      <div className="bco-linha-topo">
                        <span className="bco-data">{dataCurta(e.data)}</span>
                        {/* Na parcelada, o número do banco é o de UMA parcela. Riscado
                            fica o total, que é o que a compra custava; a conta vem
                            escrita embaixo, para ninguém ter de adivinhar qual dos dois é. */}
                        <span className="bco-valor estornado">{fmt(e.parcela ? e.valor * e.parcela.total : e.valor)}</span>
                      </div>
                      <p className="bco-desc">{e.descricao}</p>
                      {e.parcela && (
                        <p className="bco-andamento">
                          {e.parcela.total}x de {fmt(e.valor)}. O total é aproximado: a primeira parcela pode
                          ter centavos a mais.
                        </p>
                      )}
                      {e.lancadoComo ? (
                        <>
                          <p className="bco-pergunta">
                            Você tinha lançado esta compra, e ela continua nas suas contas. Quer apagar o lançamento?
                          </p>
                          <div className="bco-acoes">
                            <button className="bco-btn principal" onClick={() => apagarLancamentoEstornado(e)} disabled={ocupado === e.id}>
                              Apagar lançamento
                            </button>
                            <button className="bco-btn" onClick={() => dispensarEstorno(e)} disabled={ocupado === e.id}>
                              Manter
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="bco-acoes">
                          <button className="bco-btn" onClick={() => dispensarEstorno(e)} disabled={ocupado === e.id}>
                            Ok, entendi
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </section>
              )}

              {semMetodo.length > 0 && (
                <section id="bco-metodo">
                  <h3 className="bco-subtitulo">Que método de pagamento é este?</h3>
                  <p className="bco-nota">
                    Respondendo uma vez, todo lançamento desta conta já entra com o método certo.
                    Enquanto você não confirmar, nada desta conta é lançado.
                  </p>
                  {semMetodo.map(conta => (
                    <div className="bco-item" key={conta.id}>
                      <p className="bco-desc">
                        {conta.nome}
                        <em className="bco-parcela"> · {conta.tipo === 'CREDIT' ? 'cartão de crédito' : 'conta corrente'}</em>
                      </p>
                      <div className="bco-escolha">
                        <select
                          value={metodoDaConta[conta.id] ?? CADASTRAR}
                          onChange={e => setMetodoDaConta(x => ({ ...x, [conta.id]: e.target.value }))}
                          aria-label={`Método de pagamento de ${conta.nome}`}
                        >
                          <option value={CADASTRAR}>Cadastrar “{conta.nome}”</option>
                          {paymentMethods.map(p => <option key={p.id} value={p.id}>É o meu “{p.name}”</option>)}
                        </select>
                      </div>
                      <div className="bco-acoes">
                        <button className="bco-btn principal" onClick={() => ligarConta(conta)} disabled={ocupado === `conta-${conta.id}`}>
                          Confirmar
                        </button>
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {(deFatura.length > 0 || provaveis.length > 0 || semCategoria.length > 0) && (
                <section>
                  <h3 className="bco-subtitulo">
                    Precisa de você <span className="bco-conta">{deFatura.length + provaveis.length + semCategoria.length}</span>
                  </h3>

                  {/*
                    Pagamento de fatura (13/10). Sem "Lançar": as compras do
                    cartão já contam, e lançar a fatura as contaria de novo.
                    Confirmar só dá a fatura como paga.
                  */}
                  {deFatura.map(m => {
                    const o = opcaoSugerida(m.fatura);
                    return (
                      <div className="bco-item" key={m.id}>
                        {topo(m)}
                        {escolhaDeFatura[m.id] ? (
                          <>
                            <p className="bco-pergunta">De qual cartão e de que mês é esta fatura?</p>
                            {escolherFatura(m)}
                          </>
                        ) : (
                          <>
                            <p className="bco-pergunta">
                              {o
                                ? <>Parece o pagamento da fatura do <strong>{o.nome}</strong> de {mesPorExtenso(o.mes)}. {consequenciaDe(o, fmt)}</>
                                : cartoesDeFatura.length > 0
                                  ? 'Parece pagamento de fatura de cartão. De qual cartão?'
                                  : 'O banco diz que é pagamento de fatura de cartão. Se as compras desse cartão já estão lançadas, ignore; lançar contaria duas vezes.'}
                            </p>
                            <p className="bco-nota bco-fatura-nota">Não vira lançamento: as compras do cartão já contam uma a uma.</p>
                            <div className="bco-acoes">
                              {o && (
                                <button
                                  className="bco-btn principal"
                                  onClick={() => vincularFatura(m, o.metodoId, o.mes, o.nome)}
                                  disabled={ocupado === m.id}
                                >
                                  É a fatura
                                </button>
                              )}
                              {cartoesDeFatura.length > 0 && (
                                <button className={`bco-btn${o ? '' : ' principal'}`} onClick={() => abrirEscolhaDeFatura(m)} disabled={ocupado === m.id}>
                                  {o ? 'Outro cartão ou mês' : 'Escolher o cartão'}
                                </button>
                              )}
                              <button
                                className="bco-btn"
                                onClick={() => setNaoEFatura(antes => new Set(antes).add(m.id))}
                                disabled={ocupado === m.id}
                              >
                                Não é fatura
                              </button>
                              <button className="bco-btn" onClick={() => ignorar(m)} disabled={ocupado === m.id}>
                                Ignorar
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}

                  {provaveis.map(m => {
                    const t = lancamento(m.casadoCom);
                    return (
                      <div className="bco-item" key={m.id}>
                        {topo(m)}
                        <p className="bco-pergunta">
                          {t
                            ? <>É o seu <strong>“{t.description}”</strong>?</>
                            : 'Você já lançou algo com este valor nesta data. É o mesmo?'}
                        </p>
                        <div className="bco-acoes">
                          <button className="bco-btn" onClick={() => simples(m, confirmarMesmoDoBanco)} disabled={ocupado === m.id}>
                            É o mesmo
                          </button>
                          <button className="bco-btn" onClick={() => simples(m, separarMovimentoDoBanco)} disabled={ocupado === m.id}>
                            Não, é outro
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {semCategoria.map(m => (
                    <div className="bco-item" key={m.id}>
                      {topo(m)}
                      <p className="bco-pergunta">
                        Não sei a categoria. Diga uma vez e eu lembro para os próximos.
                      </p>
                      <div className="bco-escolha">
                        {seletorDeCategoria(m, edicoes[m.id]?.categoryId ?? '', id => editar(m, { categoryId: id }))}
                      </div>
                      <div className="bco-acoes">
                        {botaoLancar(m)}
                        <button className="bco-btn" onClick={() => ignorar(m)} disabled={ocupado === m.id}>
                          Ignorar
                        </button>
                      </div>
                      {portaDaFatura(m)}
                    </div>
                  ))}
                </section>
              )}

              {prontos.length > 0 && (
                <section>
                  <h3 className="bco-subtitulo">Novos <span className="bco-conta">{prontos.length}</span></h3>
                  {marcaveis.length > 1 && (
                    <label className="bco-marcar-todos">
                      <input
                        type="checkbox"
                        checked={selecionados.length === marcaveis.length}
                        onChange={e => setMarcados(e.target.checked ? new Set(marcaveis.map(m => m.id)) : new Set())}
                      />
                      <span>Selecionar todos ({marcaveis.length})</span>
                    </label>
                  )}
                  {prontos.map(m => {
                    const ed = edicoes[m.id] ?? {};
                    const categoria = ed.categoryId !== undefined ? ed.categoryId : (m.sugestao.categoryId ?? '');
                    const rascunho = rascunhos[m.id];
                    const rascunhar = (mudanca: Partial<{ nome: string; categoryId: string }>) =>
                      setRascunhos(r => ({ ...r, [m.id]: { ...r[m.id], ...mudanca } }));
                    const fecharRascunho = () => {
                      setRascunhos(r => Object.fromEntries(Object.entries(r).filter(([id]) => id !== m.id)));
                      if (nova?.de === m.id) setNova(null);
                    };
                    const podeMarcar = marcaveis.some(x => x.id === m.id);
                    return (
                      <div className="bco-item com-marca" key={m.id}>
                        <input
                          type="checkbox"
                          className="bco-marca"
                          aria-label={`Selecionar ${ed.nome?.trim() || m.sugestao.nome}`}
                          checked={podeMarcar && marcados.has(m.id)}
                          disabled={!podeMarcar}
                          onChange={e => setMarcados(antes => {
                            const depois = new Set(antes);
                            if (e.target.checked) depois.add(m.id); else depois.delete(m.id);
                            return depois;
                          })}
                        />
                        <div className="bco-item-corpo">
                        {topo(m)}
                        {rascunho ? (
                          <>
                            <div className="bco-escolha">
                              <input
                                type="text"
                                maxLength={LIMITES.descricao}
                                aria-label="Nome do lançamento"
                                value={rascunho.nome}
                                onChange={e => rascunhar({ nome: e.target.value })}
                              />
                              {seletorDeCategoria(m, rascunho.categoryId, id => rascunhar({ categoryId: id }))}
                            </div>
                            <div className="bco-acoes">
                              <button
                                className="bco-btn principal"
                                disabled={!rascunho.nome.trim()}
                                onClick={() => {
                                  editar(m, { nome: rascunho.nome.trim(), categoryId: rascunho.categoryId });
                                  fecharRascunho();
                                }}
                              >
                                Confirmar mudança
                              </button>
                              <button className="bco-btn" onClick={fecharRascunho}>Cancelar</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="bco-etiquetas">
                              <span className="bco-etiqueta">{nomeDaCategoria(categoria) ?? 'Sem categoria'}</span>
                              <button
                                className="bco-link"
                                onClick={() => rascunhar({ nome: ed.nome ?? m.sugestao.nome, categoryId: categoria })}
                              >
                                mudar nome ou categoria
                              </button>
                            </p>
                            <div className="bco-acoes">
                              {botaoLancar(m)}
                              <button className="bco-btn" onClick={() => ignorar(m)} disabled={ocupado === m.id}>
                                Ignorar
                              </button>
                            </div>
                            {portaDaFatura(m)}
                          </>
                        )}
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}

              {/* Não junto de um erro: "não consegui ler" e "nada esperando"
                  na mesma tela é uma afirmação desmentindo a outra. */}
              {/* Nem junto de um aviso: "nada novo desde a última leitura"
                  já responde, e as duas frases empilhadas diziam quase o mesmo. */}
              {!erro && !aviso && dados.conexoes.length > 0 && semMetodo.length === 0 && visto.pendentes.length === 0 && estornados.length === 0 && (
                <p className="bco-texto bco-em-dia">Nada esperando você.</p>
              )}

              {visto.lancados.length > 0 && (
                <section>
                  <h3 className="bco-subtitulo">Lançados sozinhos</h3>
                  <p className="bco-nota">Dos últimos dias. Se a categoria estiver errada, troque: eu aprendo.</p>
                  {visto.lancados.map(m => (
                    <div className="bco-item lancado" key={m.id}>
                      {topo(m)}
                      <div className="bco-escolha">
                        {seletorDeCategoria(m, m.sugestao.categoryId ?? '', id => trocarCategoria(m, id))}
                      </div>
                      <p className="bco-etiquetas">
                        {/* Só o palpite do banco é marcado: os outros dois vieram de algo que ELA disse. */}
                        {m.sugestao.origem === 'banco' && <span className="bco-etiqueta confira">categoria pelo banco, confira</span>}
                        <button className="bco-link" onClick={() => simples(m, desfazerMovimentoDoBanco, true)} disabled={ocupado === m.id}>
                          desfazer
                        </button>
                      </p>
                    </div>
                  ))}
                </section>
              )}

              {faturasVinculadas.length > 0 && (
                <section>
                  <button className="bco-recolhido" onClick={() => setVerFaturas(v => !v)} aria-expanded={verFaturas}>
                    {verFaturas ? '▾' : '▸'} {faturasVinculadas.length === 1
                      ? '1 pagamento de fatura'
                      : `${faturasVinculadas.length} pagamentos de fatura`}
                  </button>
                  {verFaturas && faturasVinculadas.map(v => (
                    <div className="bco-item casado" key={v.id}>
                      <div className="bco-linha-topo">
                        <span className="bco-data">{dataCurta(v.data)}</span>
                        <span className="bco-valor saida">−{fmt(v.valor)}</span>
                      </div>
                      <p className="bco-desc">{v.descricao}</p>
                      <p className="bco-pergunta">
                        Fatura do <strong>{v.cartao}</strong> de {mesPorExtenso(v.mes)}
                        {v.paga ? ', paga.' : `: pago ${fmt(v.pago)} de ${fmt(v.total)}, em aberto.`}
                        {v.automatico ? ' Vinculado sozinho.' : ''}
                        {' '}
                        <button className="bco-link" onClick={() => desvincularFatura(v)} disabled={ocupado === v.id}>
                          desfazer
                        </button>
                      </p>
                    </div>
                  ))}
                </section>
              )}

              {visto.casados.length > 0 && (
                <section>
                  <button className="bco-recolhido" onClick={() => setVerCasados(v => !v)} aria-expanded={verCasados}>
                    {verCasados ? '▾' : '▸'} {visto.casados.length} que você já lançou
                  </button>
                  {verCasados && visto.casados.map(m => {
                    const t = lancamento(m.casadoCom);
                    return (
                      <div className="bco-item casado" key={m.id}>
                        {topo(m)}
                        <p className="bco-pergunta">
                          {t ? <>Casou com <strong>“{t.description}”</strong>.</> : 'Casou com um lançamento seu.'}
                          {' '}
                          <button className="bco-link" onClick={() => simples(m, separarMovimentoDoBanco)} disabled={ocupado === m.id}>
                            não é o mesmo
                          </button>
                        </p>
                      </div>
                    );
                  })}
                </section>
              )}
              {/*
                O suporte. Antes, quem dava falta de uma compra tinha de gerar
                um relatório, copiar e mandar. Agora escreve o que faltou e
                envia; o diagnóstico vai junto, sozinho, e ela vê por extenso
                o que vai. Desenho em `docs/designs/suporte-open-finance.md`.
              */}
              {suporte && dados.conexoes.length > 0 && (
                <section className="bco-suporte">
                  {!pedido.aberto ? (
                    <p className="bco-quando">
                      {suporte.pedidos.length > 0 && (
                        <>
                          Você tem {suporte.pedidos.length === 1 ? '1 pedido' : `${suporte.pedidos.length} pedidos`} em
                          análise, enviado em {dataCurta(suporte.pedidos[0].criadoEm.slice(0, 10))}.{' '}
                        </>
                      )}
                      Algo não bateu?{' '}
                      <button
                        className="bco-link"
                        onClick={() => setPedido(p => ({ ...p, aberto: true, enviado: false, conexaoId: ativa?.id ?? dados.conexoes[0].id }))}
                      >
                        Fale com o suporte
                      </button>
                      .
                    </p>
                  ) : pedido.enviado ? (
                    <div className="bco-suporte-caixa" role="status">
                      <p className="bco-desc">Recebemos o seu pedido.</p>
                      <p className="bco-nota">
                        Vamos olhar a leitura do seu banco e respondemos
                        {suporte.email ? <> pelo e-mail <strong>{suporte.email}</strong></> : ' por e-mail'}.
                      </p>
                      <div className="bco-acoes">
                        <button className="bco-btn" onClick={() => setPedido(p => ({ ...p, aberto: false, enviado: false }))}>
                          Fechar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bco-suporte-caixa">
                      <h3 className="bco-subtitulo">Fale com o suporte</h3>
                      <div className="bco-suporte-tipos" role="group" aria-label="O que aconteceu">
                        {([
                          ['faltou', 'Faltou uma transação'],
                          ['parcelada', 'Parcelada veio errada'],
                          ['outro', 'Outra coisa'],
                        ] as [TipoDePedidoDeSuporte, string][]).map(([tipo, rotulo]) => (
                          <button
                            key={tipo}
                            className={`bco-aba${pedido.tipo === tipo ? ' ativa' : ''}`}
                            aria-pressed={pedido.tipo === tipo}
                            onClick={() => setPedido(p => ({ ...p, tipo }))}
                          >
                            {rotulo}
                          </button>
                        ))}
                      </div>
                      <div className="bco-escolha bco-suporte-campos">
                        {dados.conexoes.length > 1 && (
                          <select
                            aria-label="Em qual banco"
                            value={pedido.conexaoId}
                            onChange={e => setPedido(p => ({ ...p, conexaoId: e.target.value }))}
                          >
                            {dados.conexoes.map(c => <option key={c.id} value={c.id}>{c.banco}</option>)}
                          </select>
                        )}
                        <input
                          type="text"
                          inputMode="decimal"
                          maxLength={LIMITES.data}
                          aria-label="Valor, se souber"
                          placeholder="Valor, se souber. Ex.: 63,35"
                          value={pedido.valor}
                          onChange={e => setPedido(p => ({ ...p, valor: e.target.value }))}
                        />
                      </div>
                      <textarea
                        className="bco-suporte-texto"
                        maxLength={LIMITES.textoIa}
                        aria-label="Conte o que você esperava ver"
                        placeholder="Conte o que você esperava ver. Ex.: comprei em 10x em janeiro e a parcela deste mês não apareceu."
                        value={pedido.mensagem}
                        onChange={e => setPedido(p => ({ ...p, mensagem: e.target.value }))}
                      />
                      {/* O que vai junto, por extenso e ANTES do botão: é o
                          consentimento, e ele só vale se ela puder ler. */}
                      <p className="bco-nota">
                        Vai para o suporte: o que você escreveu, e um diagnóstico da leitura deste banco,
                        com códigos e contagens. Sem nome de loja e sem valores. Se você informar um
                        valor, vão também a data, a parcela e a fatura das compras com exatamente esse
                        valor.
                      </p>
                      <div className="bco-acoes">
                        <button
                          className="bco-btn principal"
                          onClick={enviarPedido}
                          disabled={!!ocupado || pedido.mensagem.trim().length < 5}
                        >
                          Enviar
                        </button>
                        <button className="bco-btn" onClick={() => setPedido(p => ({ ...p, aberto: false }))} disabled={ocupado === 'suporte'}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/*
                As ferramentas de copiar e colar, que o formulário de cima
                substituiu. Continuam para quem atende, recolhidas: servem
                para investigar a própria conta sem abrir um pedido.
              */}
              {(admin || dados.suporte) && dados.contas.length > 0 && (
                <details className="bco-retrato">
                  <summary className="bco-recolhido">Ferramentas de diagnóstico</summary>
                  {retrato === null ? (
                    <>
                    <p className="bco-quando">
                      Suporte: faltou alguma transação?{' '}
                      <button className="bco-link" onClick={gerarRaioX} disabled={!!ocupado}>
                        Gerar raio-x da leitura
                      </button>
                      . Parcelada errada?{' '}
                      <button className="bco-link" onClick={gerarRetrato} disabled={!!ocupado}>
                        Gerar retrato do cartão
                      </button>
                      .
                    </p>
                    {/* Depois de corrigir uma regra de leitura: o Atualizar olha
                        só os últimos dias, e isto volta aos 60 da primeira. O que
                        já foi lançado ou ignorado continua como está. */}
                    <p className="bco-quando">
                      Abertura para usuários:{' '}
                      <button className="bco-link" onClick={gerarFormato} disabled={!!ocupado}>
                        Gerar formato das conexões
                      </button>
                      .
                    </p>
                    <p className="bco-quando">
                      Corrigiu uma regra de leitura?{' '}
                      <button className="bco-link" onClick={() => atualizar(visto.conexoes, true)} disabled={!!ocupado}>
                        Reler os últimos 60 dias{comAbas && ativa ? ` do ${ativa.banco}` : ''}
                      </button>
                      .
                    </p>
                    <div className="bco-escolha bco-procurar">
                      <input
                        type="text"
                        inputMode="decimal"
                        maxLength={LIMITES.data}
                        aria-label="Valor que faltou"
                        placeholder="Faltou um valor? Ex.: 63,35"
                        value={valorProcurado}
                        onChange={e => setValorProcurado(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && valorProcurado.trim()) { e.preventDefault(); procurarValor(); } }}
                      />
                      <button className="bco-btn" onClick={procurarValor} disabled={!!ocupado || !valorProcurado.trim()}>
                        Procurar no banco
                      </button>
                    </div>
                    </>
                  ) : (
                    <>
                      <h3 className="bco-subtitulo">Relatório de suporte</h3>
                      <p className="bco-nota">
                        Ferramenta de suporte. Mostra como o banco manda os dados e o que a leitura
                        fez com eles: códigos e contagens, sem nome de loja nem valor.
                      </p>
                      {/* `pre`, e não campo de texto: é para ler e copiar, não para digitar. */}
                      <pre className="bco-retrato-texto" tabIndex={0} aria-label="Retrato do cartão">{retrato}</pre>
                      <div className="bco-acoes">
                        <button className="bco-btn principal" onClick={copiarRetrato}>Copiar</button>
                        <button className="bco-btn" onClick={() => setRetrato(null)}>Fechar</button>
                      </div>
                    </>
                  )}
                </details>
              )}
            </>
          )}
        </div>
        {/*
          Fora da área que rola: a barra fica no pé do modal enquanto houver
          seleção, e ela confirma o lote sem voltar ao fim da lista.
        */}
        {trava && (
          <div className={`bco-trava ${trava.fase}`} role="status" aria-live="assertive">
            {trava.fase === 'lancando' ? (
              <span className="bco-trava-roda" aria-hidden="true" />
            ) : (
              <svg className="bco-trava-check" viewBox="0 0 52 52" aria-hidden="true">
                <circle cx="26" cy="26" r="24" />
                <path d="M15 27l8 8 15-17" />
              </svg>
            )}
            <span className="bco-trava-texto">{trava.texto}</span>
          </div>
        )}
        {!ajuda && selecionados.length > 0 && (
          <div className="bco-lote" role="region" aria-label="Lançar selecionados">
            <span className="bco-lote-conta">
              {selecionados.length === 1 ? '1 selecionado' : `${selecionados.length} selecionados`}
            </span>
            <button className="bco-btn" onClick={() => setMarcados(new Set())} disabled={ocupado === 'lote'}>
              Limpar
            </button>
            <button className="bco-btn principal" onClick={() => lancarEmLote(selecionados)} disabled={ocupado === 'lote'}>
              {`Lançar ${selecionados.length}`}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CaixaDoBanco;
